import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import type { MediaType } from '@ember/shared';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useSessionSocket } from '@/hooks/use-session-socket';
import { useSessionPlayer } from '@/hooks/use-session-player';
import { LobbyCard } from '@/components/game/lobby-card';
import { RoundCard } from '@/components/game/round-card';
import { EndCard } from '@/components/game/end-card';
import { ConnectionBanner } from '@/components/game/connection-banner';
import { LobbySkeleton, RoundSkeleton } from '@/components/game/round-skeleton';

/**
 * Page de jeu unifiee qui change d'affichage selon le status de la session
 * ET le curseur LOCAL du joueur (chaque joueur avance a son rythme apres reveal) :
 *
 *   WAITING                                  -> <LobbyCard />
 *   ACTIVE + curseur <= total + round dispo  -> <RoundCard />
 *   ACTIVE + curseur > total (fini en avance)-> <EndCard partnerStillPlaying />
 *   FINISHED                                 -> <EndCard />
 *
 * Transitions entre etats : AnimatePresence mode="wait" pour qu'un seul ecran
 * soit anime a la fois (evite chevauchements + sauts de layout sur mobile).
 */
export function PlayPage() {
  const { shortCode } = useParams<{ shortCode: string }>();
  const navigate = useNavigate();
  const { playerId } = useSessionPlayer(shortCode);
  const {
    session,
    round,
    rounds,
    hasFinishedLocally,
    loading,
    error,
    connectionStatus,
    startSession,
    submitAnswer,
    nextRound,
  } = useSessionSocket(shortCode, playerId);

  // Si on arrive sur /play/:code sans avoir rejoint (pas de playerId stocke),
  // on redirige vers /s/:code pour faire l'onboarding. Evite l'etat "spectateur".
  useEffect(() => {
    if (!loading && session && session.status === 'WAITING' && !playerId) {
      navigate(`/s/${shortCode}`, { replace: true });
    }
  }, [loading, session, playerId, shortCode, navigate]);

  if (loading) {
    return (
      <div className="max-w-lg mx-auto">
        <ConnectionBanner status={connectionStatus} />
        <LobbySkeleton />
      </div>
    );
  }
  if (error && !session) {
    return (
      <div className="max-w-md mx-auto text-center space-y-4">
        <h1 className="text-2xl font-bold">Problème</h1>
        <p className="text-muted-foreground">{error.message}</p>
        <Button className="w-full sm:w-auto min-h-[44px]" onClick={() => navigate('/')}>
          Retour à l'accueil
        </Button>
      </div>
    );
  }
  if (!session) return null;

  // Affichage EndCard si :
  //   - la session est FINISHED (les 2 joueurs ont fini tous les rounds, auto-finish serveur)
  //   - OU le joueur local a depasse son curseur (il a fini en avance, son partenaire est encore en train de jouer)
  const showEndCard = session.status === 'FINISHED' || hasFinishedLocally;
  const partnerStillPlaying = session.status === 'ACTIVE' && hasFinishedLocally;

  return (
    <div className="max-w-lg mx-auto">
      <ConnectionBanner status={connectionStatus} />

      <AnimatePresence mode="wait">
        {session.status === 'WAITING' && (
          <motion.div
            key="lobby"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <LobbyCard session={session} onStart={startSession} />
          </motion.div>
        )}

        {session.status === 'ACTIVE' && !showEndCard && round && (
          <motion.div
            key="active"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <RoundCard
              round={round}
              playerId={playerId}
              mode={session.mode}
              otherPlayerPseudo={session.players.find((p) => p.id !== playerId)?.pseudo}
              onSubmit={async (text, guessText, mediaFile, mediaType) => {
                if (!playerId || !shortCode) return;
                let mediaFilename: string | undefined;
                let finalMediaType: MediaType | undefined;
                if (mediaFile && mediaType) {
                  // Upload AVANT le submit socket : on a besoin du filename retourne.
                  // En cas d'echec, l'erreur remonte dans RoundCard via le throw.
                  const uploaded = await api.uploadMedia(shortCode, playerId, mediaType, mediaFile);
                  mediaFilename = uploaded.filename;
                  finalMediaType = mediaType;
                }
                submitAnswer(round.id, playerId, text, guessText, mediaFilename, finalMediaType);
              }}
              onNext={nextRound}
            />
          </motion.div>
        )}

        {session.status === 'ACTIVE' && !showEndCard && !round && (
          <motion.div
            key="active-loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <RoundSkeleton />
          </motion.div>
        )}

        {showEndCard && (
          <motion.div
            key="end"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <EndCard session={session} rounds={rounds} partnerStillPlaying={partnerStillPlaying} />
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <p className="text-sm text-destructive text-center mt-6" role="alert">
          {error.message}
        </p>
      )}
    </div>
  );
}
